import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type {
  VaryMutator,
  VaryOperator,
} from '@/features/legends/effects/effects'
import {
  MutatorEditorViewModelBase,
  type MutatorEditorViewModelOptions,
} from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'

/**
 * Increments or decrements number variables by a formula's result.
 */
export class VaryMutatorEditorViewModel extends MutatorEditorViewModelBase<VaryMutator> {
  private readonly _schema: IValidationSchema<VaryMutatorEditorViewModel> = {
    ...this.targetsSchema,
    expr: [func(() => this.expr.analyze(), 'There are formula errors.')],
  }

  readonly type = 'Vary'

  /**
   * The amount to change the variables by.
   */
  readonly expr: RynoEditorViewModel

  @observable accessor op: VaryOperator = 'Inc'

  readonly validation = validationContext(this, this._schema)

  constructor(options: MutatorEditorViewModelOptions) {
    super(options, 'Please select the variable(s) you wish to change.')
    this.expr = new RynoEditorViewModel({
      required: true,
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => Types.number,
    })
    this.lifecycle.manage(this.expr)
  }

  /**
   * Formulas can use `Value`, the variable's current value.
   */
  @computed
  get symbolTable() {
    return this._symbolTableWithValue(Types.number)
  }

  /**
   * Sets whether the variables are incremented or decremented.
   */
  @action.bound
  setOperator(op: VaryOperator) {
    this.op = op
  }

  /** @inheritDoc */
  @action
  hydrate(mutator: VaryMutator) {
    super.hydrate(mutator)
    this.expr.setValue(mutator.expr)
    if (mutator.op) {
      this.op = mutator.op
    }
  }

  /** @inheritDoc */
  toJSON(): VaryMutator {
    return {
      type: this.type,
      targets: this.targets.toJSON(),
      op: this.op,
      expr: this.expr.toJSON(),
    }
  }

  /** @inheritDoc */
  protected _isCompatibleTarget(
    { type }: EntityDescriptor,
    valueType: RynoType | null,
  ) {
    return type === 'Variable' && Types.isNumberType(valueType)
  }
}
